(function () {
  "use strict";

  const MODAL_ID = "lens-configurator-v2-modal";
  const STYLE_ID = "joye-lens-education-style";
  const HIGH_RX_THRESHOLD = 6;
  const STRONG_RX_THRESHOLD = 8;
  let scheduled = false;

  const rxState = {
    odSph: null,
    odCyl: 0,
    osSph: null,
    osCyl: 0
  };

  const LINKS = {
    lensType: {
      text: "Single vision or progressive? Compare lens types →",
      href: "/blogs/eyewear-guide/single-vision-vs-progressive-lenses"
    },
    prescription: {
      text: "Need help with SPH, CYL, AXIS or PD? Read the prescription guide →",
      href: "/blogs/eyewear-guide/how-to-read-glasses-prescription"
    },
    lensPackage: {
      text: "Compare photochromic, tinted & polarized lenses →",
      href: "/blogs/eyewear-guide/photochromic-vs-tinted-vs-polarized-lenses"
    },
    index: {
      text: "Not sure which index you need? View the lens index guide →",
      rimlessText: "Rimless starts at 1.60. See why lens index matters →",
      href: "/blogs/eyewear-guide/lens-index-guide-156-160-167-174"
    }
  };

  function isRimless() {
    return Boolean(document.querySelector('[data-lc-root][data-lc-rimless="true"]'));
  }

  function parseRxNumber(value) {
    const parsed = Number.parseFloat(String(value || "").trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  function eyeStrength(sphere, cylinder) {
    if (sphere === null) return null;
    const cyl = cylinder ?? 0;
    return Math.max(Math.abs(sphere), Math.abs(sphere + cyl));
  }

  function prescriptionStrength() {
    const values = [
      eyeStrength(rxState.odSph, rxState.odCyl),
      eyeStrength(rxState.osSph, rxState.osCyl)
    ].filter((value) => value !== null && Number.isFinite(value));

    return values.length ? Math.max(...values) : 0;
  }

  function recommendationLevel() {
    const strength = prescriptionStrength();
    if (strength >= STRONG_RX_THRESHOLD) return "strong";
    if (strength >= HIGH_RX_THRESHOLD) return "high";
    return "normal";
  }

  function setRxState(key, value) {
    const parsed = parseRxNumber(value);
    rxState[key] = key.endsWith("Cyl") ? (parsed ?? 0) : parsed;
  }

  function capturePrescriptionFromModal(modal) {
    ["odSph", "odCyl", "osSph", "osCyl"].forEach((key) => {
      const field = modal.querySelector(`[data-lc-rx="${key}"]`);
      if (field) setRxState(key, field.value);
    });
  }

  function capturePrescriptionInput(event) {
    const field = event.target.closest(
      '[data-lc-rx="odSph"], [data-lc-rx="odCyl"], [data-lc-rx="osSph"], [data-lc-rx="osCyl"]'
    );
    if (!field) return;
    setRxState(field.dataset.lcRx, field.value);
    scheduleEducation();
  }

  function suppressLegacyBlueLightIndexes(modal) {
    modal.querySelectorAll('[data-lc-group="index"][data-lc-id$="_bluelight"]').forEach((card) => {
      card.hidden = true;
      card.disabled = true;
      card.setAttribute("aria-hidden", "true");
      card.style.setProperty("display", "none", "important");

      const section = card.closest(".lc-v2-option-section");
      const title = section?.querySelector(".lc-v2-option-section__title")?.textContent.trim();
      if (section && title === "Blue Light Index") {
        section.hidden = true;
        section.setAttribute("aria-hidden", "true");
        section.style.setProperty("display", "none", "important");
        section.dataset.lcLegacyBlueIndex = "true";
      }
    });
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .lc-v2-education {
        margin: .65rem 0 0;
        color: #5d695f;
        font-size: 1.2rem;
        line-height: 1.4;
      }

      .lc-v2-education--top {
        margin: 0 0 1rem;
        padding: .75rem .9rem;
        border-radius: .9rem;
        background: #f6f9f4;
      }

      .lc-v2-education a {
        color: #4f6e54;
        font-weight: 650;
        text-decoration: underline;
        text-underline-offset: .2rem;
      }

      .lc-v2-education a:hover {
        color: #344b38;
      }

      .lc-v2-high-rx {
        margin: .75rem 0 0;
        padding: .8rem .9rem;
        border: 1px solid rgba(95, 125, 99, .28);
        border-radius: .9rem;
        background: #f7faf5;
      }

      .lc-v2-high-rx strong {
        display: block;
        color: #314b36;
        font-size: 1.15rem;
        line-height: 1.3;
      }

      .lc-v2-high-rx span {
        display: block;
        margin-top: .25rem;
        color: #657067;
        font-size: 1.1rem;
        line-height: 1.4;
      }

      .lc-v2-high-rx-badge {
        display: inline-flex !important;
        width: max-content;
        align-items: center;
        margin: 0 0 .4rem;
        padding: .2rem .5rem;
        border-radius: 999px;
        background: #eef5eb;
        color: #4f6e54;
        font-size: .95rem;
        font-weight: 700;
        line-height: 1.2;
      }

      @media screen and (max-width: 749px) {
        .lc-v2-education {
          margin-top: .55rem;
          font-size: 1.15rem;
          line-height: 1.35;
        }

        .lc-v2-education--top {
          margin: 0 0 .8rem;
          padding: .65rem .8rem;
        }

        .lc-v2-high-rx {
          margin-top: .6rem;
          padding: .7rem .8rem;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function createEducation(key, text, href, top) {
    const wrap = document.createElement("p");
    wrap.className = `lc-v2-education${top ? " lc-v2-education--top" : ""}`;
    wrap.dataset.lcEducation = key;

    const link = document.createElement("a");
    link.href = href;
    link.textContent = text;
    link.setAttribute("aria-label", text.replace(" →", ""));

    wrap.appendChild(link);
    return wrap;
  }

  function addTopEducation(panel, key, config) {
    if (panel.querySelector(`[data-lc-education="${key}"]`)) return;
    panel.prepend(createEducation(key, config.text, config.href, true));
  }

  function findIndexSection(panel) {
    return Array.from(panel.querySelectorAll(".lc-v2-option-section")).find((section) => {
      const title = section.querySelector(".lc-v2-option-section__title");
      return title && title.textContent.trim() === "Lens Index";
    }) || null;
  }

  function addIndexEducation(panel) {
    const key = "index";
    if (panel.querySelector(`[data-lc-education="${key}"]`)) return;

    const indexSection = findIndexSection(panel);
    if (!indexSection) return;

    const help = indexSection.querySelector(".lc-v2-option-section__help");
    const head = indexSection.querySelector(".lc-v2-option-section__head") || indexSection;
    const text = isRimless() ? LINKS.index.rimlessText : LINKS.index.text;
    const education = createEducation(key, text, LINKS.index.href, false);

    if (help && help.parentNode) {
      help.insertAdjacentElement("afterend", education);
    } else {
      head.appendChild(education);
    }
  }

  function addHighRxRecommendation(panel) {
    // Rimless owns its High-RX presentation in lens-configurator-high-rx-rules.js.
    // This avoids duplicate notices while keeping one canonical 6D/8D recommendation model.
    if (isRimless()) return;

    const level = recommendationLevel();
    if (level === "normal") return;

    const indexSection = findIndexSection(panel);
    if (!indexSection) return;

    if (!indexSection.querySelector("[data-lc-high-rx]")) {
      const note = document.createElement("div");
      note.className = "lc-v2-high-rx";
      note.dataset.lcHighRx = "true";
      note.setAttribute("role", "note");

      const title = document.createElement("strong");
      title.textContent = "High prescription recommendation";

      const copy = document.createElement("span");
      copy.textContent = level === "strong"
        ? "For a thinner lens profile, consider 1.67 or 1.74; 1.74 is the thinnest option. 1.60 remains available. Final suitability depends on your full prescription, PD, and frame size."
        : "Consider 1.67 or 1.74 for a slimmer lens profile. 1.60 remains available. Final thickness and availability depend on your full prescription, PD, and frame size.";

      note.append(title, copy);

      const education = indexSection.querySelector('[data-lc-education="index"]');
      const head = indexSection.querySelector(".lc-v2-option-section__head") || indexSection;
      if (education) education.insertAdjacentElement("afterend", note);
      else head.appendChild(note);
    }

    const badges = {
      "1.60": level === "strong" ? "May be noticeably thicker" : "May be thicker",
      "1.67": "Recommended",
      "1.74": "Thinnest option"
    };

    indexSection.querySelectorAll('[data-lc-group="index"]').forEach((card) => {
      const indexId = card.dataset.lcId || "";
      const label = badges[indexId];
      if (!label || card.querySelector("[data-lc-high-rx-badge]")) return;

      const badge = document.createElement("span");
      badge.className = "lc-v2-high-rx-badge";
      badge.dataset.lcHighRxBadge = "true";
      badge.textContent = label;

      const content = card.querySelector(".lc-v2-choice__content") || card;
      content.prepend(badge);
    });
  }

  function applyEducation() {
    scheduled = false;
    ensureStyles();

    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    suppressLegacyBlueLightIndexes(modal);
    capturePrescriptionFromModal(modal);

    const title = modal.querySelector("[data-lc-step-title]")?.textContent.trim();
    const panel = modal.querySelector("[data-lc-body] .lc-v2-step-panel");
    if (!title || !panel) return;

    if (title === "Choose prescription type") {
      addTopEducation(panel, "lens-type", LINKS.lensType);
      return;
    }

    if (title === "Enter prescription") {
      addTopEducation(panel, "prescription", LINKS.prescription);
      return;
    }

    if (title === "Choose lens package") {
      addTopEducation(panel, "lens-package", LINKS.lensPackage);
      return;
    }

    if (title === "Choose index and add-ons") {
      addIndexEducation(panel);
      addHighRxRecommendation(panel);
    }
  }

  function scheduleEducation() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(applyEducation);
  }

  function init() {
    ensureStyles();
    document.addEventListener("input", capturePrescriptionInput, true);
    document.addEventListener("change", capturePrescriptionInput, true);

    const observer = new MutationObserver(scheduleEducation);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
    scheduleEducation();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
